import { synthesizeBlob } from './voxcpm-client';
import { synthesizeVoaiBlob, type VoaiSynthesizePayload } from './voai-client';
import { synthesizeBrainserverBlob } from './brainserver-client';
import { splitForTts } from './tts-chunking';

// Note: voxcpm-client uses getEmbedApiContext() internally to find the
// API base URL + auth. Callers (admin-ui, embed-static) MUST register
// their context via setEmbedApiContext() before invoking speak().

/**
 * Provider-agnostic "make the avatar say this string" helper.
 *
 * The model3d shader runtime exposes two paths to drive its mouth:
 *  - `controller.speakText(text)` — uses browser SpeechSynthesis;
 *    audio output isn't exposed so the runtime fakes a sinusoidal RMS
 *    that doesn't match the actual phonemes (Google 小姐-style).
 *  - `controller.attachAudio(audioElement)` — wires the element through
 *    a real `AnalyserNode` so the picker sees the true RMS of any
 *    `<audio>` source. Used for VoxCPM2-via-proxy.
 *
 * `speak()` routes by the character's `provider` flag (from public-info)
 * and resolves all upstream details server-side via the character-keyed
 * proxy. The browser never sees an upstream URL or credential.
 *
 * Per embed-streaming-tts-chunks: for the voxcpm path, the reply text
 * is split at sentence boundaries into N chunks, all chunks are
 * synthesised in parallel, and they play back-to-back through the
 * SAME audio element. This drops time-to-first-audio from
 * O(synth(full reply)) to O(synth(chunk 0)) — typically 4x faster
 * for multi-sentence replies.
 */

export interface SpeakController {
  speakText(text: string): Promise<void>;
  attachAudio(source: HTMLAudioElement | MediaStream): Promise<void>;
  detachAudio(): void;
}

export interface SpeakOptions {
  controller: SpeakController;
  audioEl: HTMLAudioElement;
  text: string;
  /** Character id used to route through the admin-api character-keyed proxy. */
  characterId: string;
  /** Public-safe provider enum from `public-info.voice.provider`. */
  provider: 'browser' | 'voxcpm' | 'voai' | 'brainserver';
  /**
   * Optional voice/style/instruct overrides. When omitted, the server
   * uses the persisted character config. Used by the Voice tab's 試聽
   * button to test an unsaved combination.
   */
  voxcpmOverrides?: { voice?: string; style?: string; instructText?: string };
  /**
   * Optional VOAI override fields — same purpose as `voxcpmOverrides`
   * but for the `provider === 'voai'` branch. Mirrors the subset of
   * `voice.tts.voai` fields that the admin 試聽 button may tweak
   * without persisting.
   */
  voaiOverrides?: Pick<VoaiSynthesizePayload, 'speaker' | 'style' | 'version' | 'speed' | 'pitch_shift' | 'style_weight' | 'breath_pause' | 'outputFormat'>;
  /**
   * Cancel mid-flight. Per `embed-mid-reply-interrupt`: aborting the
   * signal stops all in-flight synth fetches, pauses + resets the
   * audio element, detaches the controller analyser, and resolves
   * speak() within a few ms so the voice loop can yield to the user.
   */
  signal?: AbortSignal;
  /**
   * Per voice-loop iter 5 (`tts_fallback_chain`): when set + the
   * configured provider's chunk-0 synth fails before any audio plays,
   * fall back to `controller.speakText(text)` (browser SpeechSynthesis)
   * so the user at least HEARS the reply. Once chunk 0 plays, later
   * chunk failures keep the existing throw behavior.
   */
  fallbackToBrowser?: boolean;
  /**
   * Per voice-loop iter 7 (`pregenerate_greeting`): a pre-synthesised
   * blob covering the FULL reply. When set, speak() bypasses chunked
   * synth entirely + plays this single blob through the audio
   * element. Used by the greeting flow which knows the text ahead
   * of time and prewarms the synth in the background.
   */
  precomputedBlob?: Blob;
  /**
   * Per voice-loop iter 14 (`adaptive_tts_chunks`): keep chunk 0 as
   * a single sentence (preserves TTFA) but merge later chunks in
   * pairs. Halves synth round-trips past the first chunk. Caller
   * threads through useVoiceFlag('adaptive_tts_chunks').
   */
  adaptiveChunkSize?: boolean;
}

/**
 * Single in-flight invariant: any prior playback on the supplied
 * audioEl is paused, the controller analyser is detached, and the
 * previous blob URL (cached on the element) is revoked before the new
 * playback begins. Otherwise rapid back-to-back replies would leak
 * MediaElementSourceNodes onto the analyser's AudioContext.
 */
function tearDown(audioEl: HTMLAudioElement, controller: SpeakController): void {
  try { audioEl.pause(); } catch { /* noop */ }
  try { controller.detachAudio(); } catch { /* noop */ }
  const prior = (audioEl as unknown as { __nxorbitBlobUrl?: string }).__nxorbitBlobUrl;
  if (prior) {
    try { URL.revokeObjectURL(prior); } catch { /* noop */ }
    (audioEl as unknown as { __nxorbitBlobUrl?: string }).__nxorbitBlobUrl = undefined;
  }
}

export async function speak(opts: SpeakOptions): Promise<void> {
  const { controller, audioEl, text, characterId, provider, voxcpmOverrides, voaiOverrides, signal, fallbackToBrowser, precomputedBlob, adaptiveChunkSize } = opts;
  console.log('[speak] entry', { provider, textLen: text.length, characterId, hasSignal: !!signal, fallbackToBrowser: !!fallbackToBrowser, hasPrecomputed: !!precomputedBlob, adaptiveChunkSize: !!adaptiveChunkSize });

  // Pre-abort check — if the caller already aborted before invoking,
  // bail without doing anything.
  if (signal?.aborted) {
    console.log('[speak] signal already aborted at entry');
    return;
  }

  // Browser fallback — no real audio source to attach. The runtime's
  // synthetic-RMS path drives the mouth.
  if (provider !== 'voxcpm' && provider !== 'voai' && provider !== 'brainserver') {
    console.log('[speak] provider == browser → using browser SpeechSynthesis');
    tearDown(audioEl, controller);
    await controller.speakText(text);
    return;
  }

  // voice-loop iter 7 — pre-warmed greeting blob hits this path. Skip
  // chunked synth entirely (the blob already covers the full reply)
  // and play it through the audio element with the lip-sync analyser
  // attached just like a normal TTS chunk.
  if (precomputedBlob) {
    console.log('[speak] using precomputed blob, size', precomputedBlob.size);
    tearDown(audioEl, controller);
    const url = URL.createObjectURL(precomputedBlob);
    (audioEl as unknown as { __nxorbitBlobUrl?: string }).__nxorbitBlobUrl = url;
    audioEl.src = url;
    try {
      await controller.attachAudio(audioEl);
      await playOnce(audioEl, signal);
    } finally {
      try { URL.revokeObjectURL(url); } catch { /* noop */ }
      (audioEl as unknown as { __nxorbitBlobUrl?: string }).__nxorbitBlobUrl = undefined;
      try { controller.detachAudio(); } catch { /* noop */ }
    }
    return;
  }

  // Remote TTS path: chunked-streaming pipeline. Splits the reply at
  // sentence boundaries, fans out synth in parallel, plays sequentially.
  const chunks = splitForTts(text, { adaptiveChunkSize });
  console.log('[speak] chunked', { chunks: chunks.length, sizes: chunks.map((c) => c.length), adaptive: !!adaptiveChunkSize });
  if (chunks.length === 0) return;
  return speakChunked({ chunks, fullText: text, audioEl, controller, characterId, provider, voxcpmOverrides, voaiOverrides, signal, fallbackToBrowser });
}

interface SpeakChunkedArgs {
  chunks: string[];
  /** Per voice-loop iter 5 — needed when chunk 0 fails + we want to
   *  feed the whole reply to the browser-TTS fallback. */
  fullText: string;
  audioEl: HTMLAudioElement;
  controller: SpeakController;
  characterId: string;
  provider: 'voxcpm' | 'voai' | 'brainserver';
  voxcpmOverrides?: SpeakOptions['voxcpmOverrides'];
  voaiOverrides?: SpeakOptions['voaiOverrides'];
  signal?: AbortSignal;
  fallbackToBrowser?: boolean;
}

/**
 * Parallel synthesise + sequential play. Analyser is attached ONCE at
 * the start of chunk 0 and detached ONCE at the end so the lip-sync
 * stays continuous across chunk boundaries (and we don't leak
 * MediaElementSourceNodes per chunk).
 */
async function speakChunked(args: SpeakChunkedArgs): Promise<void> {
  const { chunks, fullText, audioEl, controller, characterId, provider, voxcpmOverrides, voaiOverrides, signal, fallbackToBrowser } = args;

  tearDown(audioEl, controller);

  // Per-turn timeout — generous enough that even a 12-chunk sequence
  // with slow synth finishes inside it. Bumped from the legacy 60s
  // single-blob timeout to 120s.
  const safetyTimeout = window.setTimeout(() => {
    console.warn('[speak] safety timeout — chunked playback never finished, releasing voice loop');
    try { audioEl.pause(); } catch { /* noop */ }
    try { controller.detachAudio(); } catch { /* noop */ }
  }, 120_000);

  // Kick off synths. For voai / voxcpm we fan out in PARALLEL —
  // the upstream synth APIs accept concurrent requests so this
  // halves time-to-first-audio for multi-sentence replies.
  //
  // BrainServer is different: its 0x07 listener is single-
  // connection-at-a-time, so a second parallel TCP socket from
  // Lambda blocks in BrainServer's accept queue and ends up
  // hanging the WHOLE next turn (the prior in-flight socket
  // never resolves, holding the engine busy). Chain them
  // sequentially: chunk i+1's synth only starts once chunk i's
  // fetch resolves (whether success or failure). The for-loop
  // below STILL awaits synthPromises[i] in order so playback
  // begins as chunk 0 finishes — we just stop firing them all
  // at once on the wire.
  let synthPromises: Promise<Blob>[];
  if (provider === 'brainserver') {
    let prev: Promise<unknown> = Promise.resolve();
    synthPromises = chunks.map((c) => {
      const cur = prev.then(() => synthesizeBrainserverBlob(characterId, { text: c, signal }));
      // Subsequent chunks resume after this one settles (even on
      // failure) so a single chunk's error doesn't permanently
      // block the rest of the queue.
      prev = cur.catch(() => undefined);
      return cur;
    });
  } else {
    synthPromises = chunks.map((c) =>
      provider === 'voai'
        ? synthesizeVoaiBlob(characterId, { text: c, ...voaiOverrides, signal })
        : synthesizeBlob(characterId, { text: c, ...voxcpmOverrides, signal }),
    );
  }

  let attached = false;

  // Listen for abort once — pauses the active chunk, lets the loop
  // exit on the next iteration.
  const onAbort = (): void => {
    console.log('[speak] signal aborted — pausing chunked playback');
    try { audioEl.pause(); } catch { /* noop */ }
  };
  if (signal) signal.addEventListener('abort', onAbort, { once: true });

  try {
    // CRITICAL: playback order MUST follow original chunk index, NOT
    // the order in which synth promises resolve. We iterate
    // sequentially by index and `await synthPromises[i]` — if
    // synthPromises[3] happens to resolve before synthPromises[0]
    // (faster upstream for short chunks etc.), the loop still sits at
    // `await synthPromises[0]` until that one finishes, then i=1, etc.
    // JavaScript's `await` doesn't race; it's a per-promise suspension.
    // This is the only ordering guarantee — do NOT replace with
    // Promise.race / Promise.any / first-resolved tricks.
    for (let i = 0; i < synthPromises.length; i++) {
      if (signal?.aborted) {
        console.log('[speak] chunked: abort detected before chunk', i);
        break;
      }
      let blob: Blob;
      try {
        blob = await synthPromises[i]!;
      } catch (e) {
        if ((e as Error).name === 'AbortError' || signal?.aborted) {
          console.log('[speak] chunked: chunk', i, 'synth aborted');
          break;
        }
        console.warn('[speak] chunked: chunk', i, 'synth failed', e);
        // voice-loop iter 5 — tts_fallback_chain. If chunk 0 (the
        // first audio of this turn) fails AND we haven't attached yet
        // (no audio has played), drop to browser SpeechSynthesis so
        // the user at least HEARS the reply. Later-chunk failures keep
        // the existing throw behavior — by then some audio has played
        // and switching mid-sentence to browser TTS would jarringly
        // change the voice.
        if (fallbackToBrowser && i === 0 && !attached && controller.speakText) {
          console.warn('[speak] chunked: chunk 0 failed — falling back to browser SpeechSynthesis');
          window.clearTimeout(safetyTimeout);
          try { audioEl.pause(); } catch { /* noop */ }
          try { controller.detachAudio(); } catch { /* noop */ }
          await controller.speakText(fullText).catch((err) => {
            console.warn('[speak] browser-TTS fallback also failed', err);
          });
          return;
        }
        throw e;
      }
      if (signal?.aborted) break;
      console.log('[speak] chunked: chunk', i, 'ready, size', blob.size);

      const url = URL.createObjectURL(blob);
      (audioEl as unknown as { __nxorbitBlobUrl?: string }).__nxorbitBlobUrl = url;
      audioEl.src = url;

      try {
        if (!attached) {
          await controller.attachAudio(audioEl);
          attached = true;
        }
        await playOnce(audioEl, signal);
      } finally {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        (audioEl as unknown as { __nxorbitBlobUrl?: string }).__nxorbitBlobUrl = undefined;
      }
    }
  } finally {
    window.clearTimeout(safetyTimeout);
    if (signal) signal.removeEventListener('abort', onAbort);
    try { controller.detachAudio(); } catch { /* noop */ }
  }
}

/**
 * Plays the current `audioEl.src` and resolves on `ended`. Resolves
 * (without error) if `signal` aborts mid-play — the outer loop sees
 * the abort flag and stops the chunk sequence.
 */
function playOnce(audioEl: HTMLAudioElement, signal: AbortSignal | undefined): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      audioEl.removeEventListener('ended', finish);
      audioEl.removeEventListener('error', finish);
      if (signal) signal.removeEventListener('abort', finish);
      resolve();
    };
    audioEl.addEventListener('ended', finish, { once: true });
    audioEl.addEventListener('error', finish, { once: true });
    if (signal) signal.addEventListener('abort', finish, { once: true });

    audioEl.play().catch((e) => {
      console.warn('[speak] playOnce: play() rejected', e);
      finish();
    });
  });
}
